<script setup lang="ts">
import {computed, inject} from 'vue'
import {useContext, useLocalization} from '@hantera/portal-app'
import type {usePrincipalStore} from '@/stores/iam/principal-store'
import {Badge} from '@hantera/design-system'

const store = inject<ReturnType<typeof usePrincipalStore>>('principalStore')!
const context = useContext()
const t = useLocalization('iam.principals')

const isEditingSelf = computed(() => {
  return store.principal?.id === context.user().identityId
})

const SYSTEM_OWNER_ROLE = 'system:owner'

// Display roles (pending or actual)
const displayRoleIds = computed(() => {
  return store.pendingRoleIds ?? store.principal?.roles ?? []
})

// Get assigned roles with full details
const assignedRoles = computed(() => {
  return displayRoleIds.value
    .map((roleId) => store.roles.find((r) => r.key === roleId))
    .filter((r) => r !== undefined)
})

// Get available roles (not in display list)
const availableRoles = computed(() => {
  return store.roles.filter((r) => !displayRoleIds.value.includes(r.key))
})

function addRole(roleId: string) {
  const current = store.pendingRoleIds ?? store.principal?.roles ?? []
  if (current.includes(roleId)) return

  store.pendingRoleIds = [...current, roleId]
}

function removeRole(roleId: string) {
  if (isEditingSelf.value && roleId === SYSTEM_OWNER_ROLE) {
    store.dialogs.alert(
      t('roles.cannotRemoveOwnerRole'),
      t('roles.cannotRemoveRoleTitle')
    )
    return
  }

  const current = store.pendingRoleIds ?? store.principal?.roles ?? []
  store.pendingRoleIds = current.filter((r) => r !== roleId)
}

function canRemoveRole(roleKey: string): boolean {
  if (isEditingSelf.value && roleKey === SYSTEM_OWNER_ROLE) {
    return false
  }
  return store.canWrite
}
</script>

<template>
  <div v-if="store.principal" class="roles-tab">
    <div v-if="store.principal" class="roles-margin neat-scroll">
      <div class="roles-header">
        <h3>{{ t('roles.assignedRoles') }}</h3>
        <Badge>{{ assignedRoles.length === 1 ? t('roles.roleCountSingular') : t('roles.roleCountPlural', { count: assignedRoles.length }) }}</Badge>
      </div>

      <div v-if="assignedRoles.length === 0" class="empty-state">
        <p>{{ t('roles.noRolesAssigned') }}</p>
        <p class="hint" v-if="store.canWrite && availableRoles.length > 0">{{ t('roles.addRoleHint') }}</p>
      </div>

      <div v-else class="roles-list">
        <div v-for="role in assignedRoles" :key="role!.key" class="role-card box">
          <div class="role-info">
            <div class="role-name">{{ role!.key }}</div>
            <div class="role-description" v-if="role!.description">{{ role!.description }}</div>
          </div>
          <button
            v-if="canRemoveRole(role!.key)"
            @click="removeRole(role!.key)"
            class="btn-danger"
          >
            {{ t('roles.remove') }}
          </button>
        </div>
      </div>

      <div v-if="store.canWrite && availableRoles.length > 0" class="add-role-section">
        <div class="roles-header">
          <h3>{{ t('roles.availableRoles') }}</h3>
        </div>
        <div class="available-roles-list">
          <div v-for="role in availableRoles" :key="role.key" class="available-role-card box">
            <div class="role-info">
              <div class="role-name">{{ role.key }}</div>
              <div class="role-description" v-if="role.description">{{ role.description }}</div>
            </div>
            <button @click="addRole(role.key)">{{ t('roles.add') }}</button>
          </div>
        </div>
      </div>

    </div>
    <div v-if="store.hasRolesChanges" class="action-bar">
      <button @click="store.saveRoles" class="btn-primary">{{ t('actions.saveChanges') }}</button>
      <button @click="store.discardRoles">{{ t('actions.discard') }}</button>
    </div>
  </div>
</template>

<style scoped>
.roles-tab {
  height: 100%;
  display: grid;
  grid-template-rows: auto min-content;
  gap: 0;
}

.roles-margin {
  padding: 1.5rem;
  overflow: scroll;
}

.roles-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.empty-state {
  text-align: center;
  padding: 2rem 1.5rem;
  color: #666;
}

.empty-state p {
  margin: 0.5rem 0;
}

.empty-state .hint {
  font-size: 0.875rem;
  font-style: italic;
}

.roles-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm, 0.75rem);
  margin-bottom: 2rem;
}

.role-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
}

.role-info {
  flex: 1;
}

.role-name {
  font-weight: 600;
  font-size: 1rem;
  color: hsl(var(--color-20));
  margin-bottom: 0.25rem;
}

.role-description {
  font-size: 0.875rem;
  color: hsl(var(--color-30));
  margin-bottom: 0.25rem;
}

.role-permissions {
  font-size: 0.8125rem;
  color: #999;
}

.add-role-section {
  margin-top: 2rem;
}

.add-role-section h4 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #666;
}

.available-roles-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.available-role-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
}

.action-bar {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid hsl(var(--color-80));
  background: hsl(var(--color-90));
  position: sticky;
  bottom: 0;
  box-shadow: 0 0px 20px var(--color-shadow);
}
</style>
